/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit'
import { FetchErrorResponse } from 'api'
import {
  getDashboardAlerts,
  subscribeDashboardAlerts,
  unsubscribeDashboardAlerts,
  createUpdateScheduledReport,
  deleteScheduledReport
} from './asyncThunks'
import { ScheduledReportSlice } from './types'

const initialState: ScheduledReportSlice = {
  reportInfo: {
    status: 'idle',
    data: undefined
  },
  savingInfo: {
    saveStatus: 'idle',
    statusCode: null,
    errorInfo: '',
    failedSubscribers: []
  },
  isConfigModalOpen: false,
  subscribe: {
    isModalOpen: false,
    status: 'idle',
    type: 'subscribe'
  }
}

const scheduledReportSlice = createSlice({
  name: 'scheduledReport',
  initialState,
  reducers: {
    openScheduledReportConfigModal: (state) => {
      state.isConfigModalOpen = true
    },
    closeScheduledReportConfigModal: (state) => {
      state.isConfigModalOpen = false
    },
    openScheduledReportSubscriptionModal: (state) => {
      state.subscribe.isModalOpen = true
      state.subscribe.type = 'subscribe'
    },
    closeScheduledReportSubscriptionModal: (state) => {
      state.subscribe.isModalOpen = false
    },
    openScheduledReportUnsubscriptionModal: (state) => {
      state.subscribe.isModalOpen = true
      state.subscribe.type = 'unsubscribe'
    },
    closeScheduledReportUnsubscriptionModal: (state) => {
      state.subscribe.isModalOpen = false
    },
    closeScheduledReportErrorModal: (state) => {
      state.savingInfo = {
        saveStatus: 'idle',
        statusCode: null,
        errorInfo: '',
        failedSubscribers: []
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDashboardAlerts.pending, (state) => {
        state.reportInfo.status = 'loading'
      })
      .addCase(getDashboardAlerts.fulfilled, (state, action) => {
        state.reportInfo.status = 'succeeded'
        state.reportInfo.data = action.payload || undefined
      })
      .addCase(getDashboardAlerts.rejected, (state) => {
        state.reportInfo.status = 'failed'
      })
      // * Create/Update/Delete
      .addMatcher(
        (action) =>
          action.type === createUpdateScheduledReport.pending.type ||
          action.type === deleteScheduledReport.pending.type,
        (state) => {
          state.savingInfo.saveStatus = 'loading'
        }
      )
      .addMatcher(
        (action) =>
          action.type === createUpdateScheduledReport.fulfilled.type ||
          action.type === deleteScheduledReport.fulfilled.type,
        (state, action) => {
          state.reportInfo.data = action.payload || undefined
          state.savingInfo.saveStatus = 'succeeded'
          state.isConfigModalOpen = false
        }
      )
      .addMatcher(
        (action) =>
          action.type === createUpdateScheduledReport.rejected.type ||
          action.type === deleteScheduledReport.rejected.type,
        (state, action) => {
          state.savingInfo.saveStatus = 'failed'
          if (action?.payload) {
            const errorPayload = action.payload as FetchErrorResponse
            state.savingInfo.errorInfo = errorPayload?.error || ''
            state.savingInfo.statusCode = errorPayload?.status
            state.savingInfo.failedSubscribers = errorPayload?.info || []
          }
        }
      )
      // * Subscribe/Unsubscribe
      .addMatcher(
        (action) =>
          action.type === subscribeDashboardAlerts.pending.type ||
          action.type === unsubscribeDashboardAlerts.pending.type,
        (state) => {
          state.subscribe.status = 'loading'
        }
      )
      .addMatcher(
        (action) =>
          action.type === subscribeDashboardAlerts.fulfilled.type ||
          action.type === unsubscribeDashboardAlerts.fulfilled.type,
        (state, action) => {
          state.subscribe.status = 'succeeded'
          const [dashboardAlert] = action.payload.alerts || []
          if (dashboardAlert?.subscriptions) {
            state.reportInfo.data!.subscriptions = dashboardAlert.subscriptions
          }
        }
      )
      .addMatcher(
        (action) =>
          action.type === subscribeDashboardAlerts.rejected.type ||
          action.type === unsubscribeDashboardAlerts.rejected.type,
        (state) => {
          state.subscribe.status = 'failed'
        }
      )
  }
})

export const {
  openScheduledReportConfigModal,
  closeScheduledReportConfigModal,
  openScheduledReportSubscriptionModal,
  closeScheduledReportSubscriptionModal,
  openScheduledReportUnsubscriptionModal,
  closeScheduledReportUnsubscriptionModal,
  closeScheduledReportErrorModal
} = scheduledReportSlice.actions
export default scheduledReportSlice.reducer
